import { AxiosError } from 'axios';
import client from './client';

export type ConfidenceBandDistribution = {
  none?: number;
  zero?: number;
  low?: number;
  medium?: number;
  high?: number;
  unknown?: number;
  [key: string]: number | undefined;
};

export type SimilarItemSuggestionListItem = {
  batch_id: string;
  status: string;
  anchor_work_item_id: string;
  prior_domain: string;
  target_domain: string;
  affected_count: number;
  created_at: string;
  confidence_cue: string;
  similarity_summary: string;
};

export type SimilarItemSuggestionListResponse = {
  suggestions: SimilarItemSuggestionListItem[];
  total: number;
};

export type SimilarItemExampleItem = {
  work_item_id: string;
  title: string;
  classification_source: string;
  confidence_band: string;
  evidence_summary?: string | null;
  similarity_reason: string;
};

export type SimilarItemOverrideImpact = {
  excluded_count: number;
  statement: string;
};

export type SimilarItemUndoPosture = {
  will_record_undo_snapshot: boolean;
  retention_days: number;
  available_after: string;
  statement: string;
};

export type SimilarItemPreviewResponse = {
  batch_id: string;
  status: string;
  anchor_work_item_id: string;
  prior_domain: string;
  target_domain: string;
  affected_count: number;
  confidence_band_distribution: ConfidenceBandDistribution;
  similarity_summary: string;
  override_impact: SimilarItemOverrideImpact;
  undo_posture: SimilarItemUndoPosture;
  example_items: SimilarItemExampleItem[];
  selection_notes?: Record<string, unknown> | null;
};

export type SimilarItemDecisionResponse = {
  batch_id: string;
  status: string;
  prior_domain: string;
  target_domain: string;
  affected_count: number;
  decision_reason?: string | null;
  approved_at?: string | null;
  rejected_at?: string | null;
  deferred_at?: string | null;
};

export type SimilarItemApplyResponse = {
  batch_id: string;
  status: string;
  applied_count: number;
  skipped_count: number;
  failed_count: number;
  undo_expires_at?: string | null;
  undone_at?: string | null;
};

export type SimilarItemUndoResponse = {
  batch_id: string;
  status: string;
  restored_count: number;
  skipped_count: number;
  undone_at?: string | null;
  undo_expires_at?: string | null;
};

export type SimilarItemApiErrorDetail = {
  message?: string;
  error_class?: string;
  next_step?: string;
};

export function parseSimilarItemApiError(err: unknown, fallback: string): string {
  const detail =
    err instanceof AxiosError
      ? (err.response?.data as { detail?: string | SimilarItemApiErrorDetail } | undefined)?.detail
      : undefined;
  if (typeof detail === 'string') {
    return detail;
  }
  if (detail && typeof detail === 'object') {
    const parts = [detail.message ? String(detail.message) : fallback];
    if (detail.next_step) {
      parts.push(String(detail.next_step));
    }
    return parts.join(' ');
  }
  return fallback;
}

export function getSimilarItemErrorClass(err: unknown): string | null {
  if (!(err instanceof AxiosError)) {
    return null;
  }
  const detail = (err.response?.data as { detail?: SimilarItemApiErrorDetail } | undefined)?.detail;
  if (detail && typeof detail === 'object' && detail.error_class) {
    return String(detail.error_class);
  }
  return null;
}

export async function fetchSimilarItemSuggestions(): Promise<SimilarItemSuggestionListResponse> {
  const res = await client.get<SimilarItemSuggestionListResponse>(
    '/classification-suggestions/similar-items',
  );
  return res.data;
}

export async function fetchSimilarItemPreview(batchId: string): Promise<SimilarItemPreviewResponse> {
  const res = await client.get<SimilarItemPreviewResponse>(
    `/classification-suggestions/similar-items/${encodeURIComponent(batchId)}`,
  );
  return res.data;
}

export async function approveSimilarItemBatch(
  batchId: string,
  reason?: string,
): Promise<SimilarItemDecisionResponse> {
  const res = await client.post<SimilarItemDecisionResponse>(
    `/classification-suggestions/similar-items/${encodeURIComponent(batchId)}/approve`,
    reason ? { reason } : {},
  );
  return res.data;
}

export async function applySimilarItemBatch(batchId: string): Promise<SimilarItemApplyResponse> {
  const res = await client.post<SimilarItemApplyResponse>(
    `/classification-suggestions/similar-items/${encodeURIComponent(batchId)}/apply`,
  );
  return res.data;
}

export async function rejectSimilarItemBatch(
  batchId: string,
  reason?: string,
): Promise<SimilarItemDecisionResponse> {
  const res = await client.post<SimilarItemDecisionResponse>(
    `/classification-suggestions/similar-items/${encodeURIComponent(batchId)}/reject`,
    reason ? { reason } : {},
  );
  return res.data;
}

export async function deferSimilarItemBatch(
  batchId: string,
  reason?: string,
): Promise<SimilarItemDecisionResponse> {
  const res = await client.post<SimilarItemDecisionResponse>(
    `/classification-suggestions/similar-items/${encodeURIComponent(batchId)}/defer`,
    reason ? { reason } : {},
  );
  return res.data;
}

export async function undoSimilarItemBatch(batchId: string): Promise<SimilarItemUndoResponse> {
  const res = await client.post<SimilarItemUndoResponse>(
    `/classification-suggestions/similar-items/${encodeURIComponent(batchId)}/undo`,
  );
  return res.data;
}

/** Alias for deferSimilarItemBatch — product copy uses "Ignore for now". */
export const ignoreSimilarItemBatch = deferSimilarItemBatch;
